/**
 * The bar that says what is live, what is being edited, and what it would take to publish.
 *
 * It is the only thing on screen that is the same on every Customer Bot page, and that is
 * deliberate: the question "am I looking at the draft or at what customers are seeing?" is the one
 * an editor asks constantly, and an answer that moves around is an answer nobody reads.
 *
 * ## Validate before Publish, and needs-approval before either
 *
 * `needsApproval` comes back with the draft, so the bar can say "this needs a second pair of eyes"
 * *before* someone presses Publish and is refused. A 403 after the fact is a worse way to learn a
 * policy than a sentence beside the button.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  StatusPill,
  approveVersion,
  publishVersion,
  submitVersion,
  useSession,
  useToast,
  validateVersion,
  type ValidationReport,
} from "@nab/shared";

import { useBot } from "../config";

export function VersionBar(): JSX.Element | null {
  const { t } = useTranslation("app");
  const toast = useToast();
  const { identity } = useSession();
  const { published, draft, reload, save } = useBot();
  const [report, setReport] = useState<ValidationReport | null>(null);
  const [busy, setBusy] = useState(false);

  if (!published && !draft) return null;

  const needsApproval = (draft?.needsApproval ?? []).length > 0;
  // The author cannot be the approver, so the button is offered only to somebody else. The server
  // refuses either way — this is about not presenting a dead end.
  const canApprove =
    draft?.status === "pending_approval" &&
    draft.createdBy !== identity?.employee_id;

  async function check() {
    if (!draft) return;
    setBusy(true);
    try {
      const result = await validateVersion(draft.id);
      setReport(result);
      toast.show({
        tone: result.publishable ? "success" : "error",
        message: result.publishable
          ? t("version.valid", { count: result.warnings.length })
          : t("version.invalid", { count: result.errors.length }),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-borderSubtle bg-surfaceSunken px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="text-label-md text-textMuted">{t("version.live")}</span>
        <StatusPill tone="success">{published?.label ?? "—"}</StatusPill>
      </div>

      {draft ? (
        <>
          <div className="flex items-center gap-2">
            <span className="text-label-md text-textMuted">{t("version.draft")}</span>
            <StatusPill tone={draft.status === "pending_approval" ? "warning" : "neutral"}>
              {draft.label}
            </StatusPill>
          </div>

          {needsApproval ? (
            <span className="text-label-md text-statusWarning">
              {t("version.needsApproval", {
                areas: draft.needsApproval.join(", "),
              })}
            </span>
          ) : null}

          <div className="ms-auto flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" loading={busy} onClick={check}>
              {t("version.validate")}
            </Button>

            {needsApproval && draft.status === "draft" ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  save(() => submitVersion(draft.id), t("version.submitted"))
                }
              >
                {t("version.submit")}
              </Button>
            ) : null}

            {canApprove ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  save(() => approveVersion(draft.id), t("version.approved"))
                }
              >
                {t("version.approve")}
              </Button>
            ) : null}

            <Button
              size="sm"
              variant="primary"
              disabled={needsApproval && !draft.approvedBy}
              onClick={async () => {
                const ok = await save(
                  () => publishVersion(draft.id),
                  t("version.published"),
                );
                if (ok) {
                  setReport(null);
                  await reload();
                }
              }}
            >
              {t("version.publish")}
            </Button>
          </div>
        </>
      ) : (
        <span className="ms-auto text-label-md text-textMuted">
          {t("version.noChanges")}
        </span>
      )}

      {report && !report.publishable ? (
        <ul className="w-full list-none space-y-1 pt-2">
          {report.errors.map((finding) => (
            <li key={finding.where} className="text-body-sm text-statusDanger">
              <code className="font-mono text-label-md">{finding.where}</code> —{" "}
              {finding.message}
            </li>
          ))}
          {report.warnings.map((finding) => (
            <li key={finding.where} className="text-body-sm text-textMuted">
              <code className="font-mono text-label-md">{finding.where}</code> —{" "}
              {finding.message}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
